import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { CircularProgress, InputAdornment, Tooltip } from "@mui/material";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import type { HandleAvailabilityState } from "Hooks/Social/useHandleAvailabilityState";
import { HANDLE_MAX_LENGTH, isValidHandle } from "Schemas/handleSchema";
import { Resources, useResource } from "Translations/Resources";
import { get } from "lodash-es";
import type { JSX } from "react";
import type {
  Control,
  FieldErrors,
  FieldPath,
  FieldValues,
} from "react-hook-form";

const RESOURCES = Resources.Social.Handle;

type Props<
  TFieldValues extends FieldValues,
  TTransformedValues extends FieldValues = TFieldValues,
> = {
  control: Control<TFieldValues, unknown, TTransformedValues>;
  errors: FieldErrors<TFieldValues>;
  name: FieldPath<TFieldValues>;
  /** From useHandleAvailabilityState, driven by the host form's watched value. */
  availability: HandleAvailabilityState;
  /** Focuses the input on mount (e.g. inside an edit dialog). */
  autoFocus?: boolean;
  /**
   * Shows the "changing releases the old handle" note in the helper text. Only meaningful when the
   * persona already HAS a handle — a first-time pick has nothing to release, so it stays hidden.
   */
  isChangeNoteVisible?: boolean;
};

/**
 * Embeddable handle input: "@" prefix, live availability verdict inside the end adornment
 * (spinner while checking, check/cross with a tooltip once known) and the change-consequences
 * note in the helper text. The host form owns the schema (getHandleFieldSchema), submit and
 * the availability hook, so the field composes into wizard steps as well as the standalone
 * HandleForm.
 */
export const HandleField = <
  TFieldValues extends FieldValues,
  TTransformedValues extends FieldValues = TFieldValues,
>({
  control,
  errors,
  name,
  availability,
  autoFocus,
  isChangeNoteVisible = false,
}: Props<TFieldValues, TTransformedValues>) => {
  const { t } = useResource();

  const hasFieldError = Boolean(get(errors, name));
  const showAvailability =
    !hasFieldError &&
    !availability.isUnchanged &&
    isValidHandle(availability.normalizedInput);

  const getAvailabilityIndicator = (): JSX.Element | null => {
    if (availability.isChecking) {
      return <CircularProgress size={16} />;
    }

    if (!showAvailability) {
      return null;
    }

    if (availability.isTaken) {
      return (
        <Tooltip title={t(RESOURCES.Taken)}>
          <CancelOutlinedIcon color="error" fontSize="small" />
        </Tooltip>
      );
    }

    if (availability.isAvailable) {
      return (
        <Tooltip title={t(RESOURCES.Available)}>
          <CheckCircleOutlineIcon color="success" fontSize="small" />
        </Tooltip>
      );
    }

    return null;
  };

  const availabilityIndicator = getAvailabilityIndicator();

  return (
    <FormInput
      control={control}
      errors={errors}
      name={name}
      label={t(RESOURCES.Label)}
      placeholder={t(RESOURCES.Placeholder)}
      maxLength={HANDLE_MAX_LENGTH}
      autoComplete="off"
      autoFocus={autoFocus}
      helperText={isChangeNoteVisible ? t(RESOURCES.ChangeNote) : undefined}
      inputStartAdornment={<InputAdornment position="start">@</InputAdornment>}
      inputEndAdornment={
        availabilityIndicator ? (
          <InputAdornment position="end">
            {availabilityIndicator}
          </InputAdornment>
        ) : undefined
      }
    />
  );
};
